import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react';
import { EventInstanceModel } from '../../data/EventInstanceModel';
import { EventImageType, EventTypeModel } from '../../data/EventTypeModel';
import { SYSTEM_DEVICE, useSystemDevice } from '../../hooks/useSystemDevice';
import EventItem from './EventItem/EventItem';
import WeekItem from './WeekItem/WeekItem';
import './EventsOverview.scss';
import 'swiper/css';

export interface Props {
  className?: string;
}

const EventsOverview: React.FC<Props> = (props) => {
  const scope = 'EventsOverview';

  const uniqueWeeksData = [
    {
      number: 1,
      startDate: dayjs('2023-08-01').toDate(),
    },
    {
      number: 2,
      startDate: dayjs('2023-08-07').toDate(),
    },
    {
      number: 3,
      startDate: dayjs('2023-08-14').toDate(),
    },
    {
      number: 4,
      startDate: dayjs('2023-08-21').toDate(),
    },
  ];
  const timestampNow = Date.now();
  const initialActiveWeekIndex =
    uniqueWeeksData.length -
      1 -
      uniqueWeeksData
        .slice()
        .reverse()
        .findIndex((item) => {
          return item.startDate.valueOf() <= timestampNow;
        }) ?? uniqueWeeksData.length - 1;

  const systemDevice = useSystemDevice();
  const { t } = useTranslation();
  const [activeWeekIndex, setActiveWeekIndex] = useState(
    initialActiveWeekIndex,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [swiper, setSwiper] = useState<SwiperClass>();
  useEffect(() => {
    EventInstanceModel.loadData()
      .then(() => {
        setIsLoading(false);
      })
      .catch((error) => {
        throw error;
      });
  }, []);

  const weeksSlidesPerView = 3;
  const minNumberOfSlides = weeksSlidesPerView * 2; // Limitation by swiper in loop mode;
  let weeksData = uniqueWeeksData.slice();

  if (
    systemDevice !== SYSTEM_DEVICE.DESKTOP &&
    weeksData.length < minNumberOfSlides
  ) {
    const originalData = weeksData.slice();
    const numberOfDuplicates = Math.ceil(
      (minNumberOfSlides - originalData.length) / originalData.length,
    );
    for (let i = 0; i < numberOfDuplicates; i++) {
      weeksData = [...weeksData, ...originalData];
    }
  }

  const selectWeek = (index: number) => {
    if (swiper) {
      // Do nothing. Let swiper slide and handle the event later.
    } else {
      setActiveWeekIndex(index);
    }
  };

  const initSwiper = (swiper: SwiperClass) => {
    setSwiper(swiper);
    swiper.on('slideChangeTransitionEnd', () => {
      setActiveWeekIndex(swiper.realIndex);
    });
  };

  const eventTypes = isLoading ? [] : EventTypeModel.findAll();

  const itemsData = weeksData.map((weekData, weekIndex) => {
    const { number: weekNumber, startDate: weekStartDate } = weekData;
    const weekEndDate = dayjs(weekStartDate).endOf('week').toDate();

    const isEventInGivenWeek = (event: EventInstanceModel) => {
      const eventStartDate = event.time.start;
      return eventStartDate >= weekStartDate && eventStartDate <= weekEndDate;
    };

    const weekEventTypes = eventTypes
      .filter((eventType) => {
        return eventType.events.some(isEventInGivenWeek);
      })
      .sort((eventType1, eventType2) => {
        const firstEvent1 = eventType1.events.filter(isEventInGivenWeek)[0];
        const firstEvent2 = eventType2.events.filter(isEventInGivenWeek)[0];
        return (
          firstEvent1.time.start.valueOf() - firstEvent2.time.start.valueOf()
        );
      });
    return {
      weekItem: {
        className: `${scope}-week`,
        isActive: weekIndex === activeWeekIndex,
        onClick: () => selectWeek(weekIndex),
        startDate: weekStartDate,
        weekNumber,
      },
      eventItems: weekEventTypes.map((eventType, index) => {
        const imageType =
          systemDevice === SYSTEM_DEVICE.DESKTOP
            ? 'landscape'
            : getEventImageTypeByIndex(index, weekEventTypes.length);
        return {
          className: `${scope}-event ${scope}-event--${imageType}`,
          eventType,
          imageType,
        };
      }),
    };
  });

  const className = [scope, `${scope}--${systemDevice}`, props.className]
    .filter((className) => className)
    .join(' ');

  return (
    <section className={className}>
      <div className={`${scope}-header`}>
        <h2 className={`${scope}-title`}>{t('programs')}</h2>
        {systemDevice === SYSTEM_DEVICE.DESKTOP ? (
          <div className={`${scope}-weeks`}>
            {itemsData.map(({ weekItem }, index) => (
              <WeekItem {...weekItem} key={index} />
            ))}
          </div>
        ) : (
          <Swiper
            className={`${scope}-weeks`}
            loop={true}
            loopPreventsSliding={false}
            slideToClickedSlide={true}
            onSwiper={initSwiper}
            slidesPerView={weeksSlidesPerView}
            spaceBetween={0}
          >
            {itemsData.map(({ weekItem: { onClick, ...weekItem } }, index) => (
              <SwiperSlide key={index}>
                <WeekItem {...weekItem} onClick={onClick} />
              </SwiperSlide>
            ))}
          </Swiper>
        )}
      </div>
      <div className={`${scope}-events`}>
        {itemsData[activeWeekIndex].eventItems.map((eventItem) => (
          <EventItem {...eventItem} key={eventItem.eventType.key} />
        ))}
      </div>
    </section>
  );
};

function getEventImageTypeByIndex(
  index: number,
  totalCount: number,
): EventImageType {
  const modifiersByGroupSize: EventImageType[][] = [
    [],
    ['landscape'],
    ['square', 'square'],
    ['landscape', 'square', 'square'],
    ['landscape', 'square', 'square', 'landscape'],
    ['landscape', 'square', 'portrait', 'portrait', 'square'],
  ];
  const fullGroupSize = modifiersByGroupSize.length - 1;
  const fullGroupsCount = Math.floor(totalCount / fullGroupSize);
  const indexOfPartSizeGroup = fullGroupsCount * fullGroupSize;
  const groupSize =
    index < indexOfPartSizeGroup ? fullGroupSize : totalCount % fullGroupSize;
  const indexInGroup = index % fullGroupSize;
  return modifiersByGroupSize[groupSize][indexInGroup];
}

export default EventsOverview;
